import React, { useState, useMemo, useEffect } from "react";
import { Download, Search } from "lucide-react";
import { Ticket, useGetAllTickets } from "../lib/api/ticket"; // your hook
import TicketsSummary from "../components/tickets/TicketsSummary";
import emptyTicket from "../assets/boxEmpty.png";

// Skeleton loader
const SkeletonRow = () => (
  <tr className="animate-pulse">
    {Array.from({ length: 7 }).map((_, i) => (
      <td key={i} className="p-3">
        <div className="h-4 bg-gray-200  w-3/4"></div>
      </td>
    ))}
  </tr>
);

function slugToText(slug: string) {
  return slug
    .replace(/[-_]/g, " ")
    .replace(
      /\w\S*/g,
      (txt) => txt.charAt(0).toUpperCase() + txt.substr(1).toLowerCase()
    );
}

export default function TicketsTable() {
  const { data, isLoading } = useGetAllTickets();
  const tickets: Ticket[] = React.useMemo(() => data?.data || [], [data?.data]);
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(5);

  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: "smooth" });
  }, []);

  // Filter tickets
  const filteredTickets = useMemo(() => {
    return tickets?.filter((t) => {
      const attendeeName = `${t.attendee.firstName} ${t.attendee.lastName}`;
      return (
        attendeeName.toLowerCase().includes(search.toLowerCase()) ||
        t.attendee.email.toLowerCase().includes(search.toLowerCase()) ||
        t.orderId._id.toLowerCase().includes(search.toLowerCase())
      );
    });
  }, [search, tickets]);

  // Pagination logic
  const totalPages = Math.ceil(filteredTickets?.length / rowsPerPage);
  const startIndex = (page - 1) * rowsPerPage;
  const paginatedTickets = filteredTickets?.slice(
    startIndex,
    startIndex + rowsPerPage
  );

  // Export CSV
  const exportCSV = () => {
    const headers = [
      "Date Issued",
      "Order ID",
      "Attendee Name",
      "Email",
      "Dietary Requirements",
      "Ticket Tier",
      "Price",
      "Payment Status",
    ];
    const rows = filteredTickets?.map((t) => {
      const attendeeName = `${t.attendee.firstName} ${t.attendee.lastName}`;
      const price =
        parseFloat(t.orderId.amountTotal) / (t.orderId.quantity || 1);

      return [
        new Date(t.createdAt).toLocaleDateString(),
        t.orderId._id,
        attendeeName,
        t.attendee.email,
        t.attendee.dietaryRequirements || "N/A",
        t.orderId.tier,
        `£${(price / 100).toFixed(2)}`,
        t.orderId.paymentStatus,
      ];
    });

    const csvContent =
      "data:text/csv;charset=utf-8," +
      [headers, ...rows].map((e) => e.join(",")).join("\n");

    const link = document.createElement("a");
    link.href = encodeURI(csvContent);
    link.download = "tickets?.csv";
    link.click();
  };

  const colors: Record<string, string> = {
    paid: "bg-green-100 text-green-800",
    pending: "bg-yellow-100 text-yellow-800",
    failed: "bg-red-100 text-red-800",
    refunded: "bg-blue-100 text-blue-800",
  };

  return (
    <div className=" min-h-[90vh] ">
      <div className="max-w-7xl bg-white  mx-auto p-4">
        <TicketsSummary tickets={tickets} />
        <div className="p-4 bg-gray-50">
          {/* Top bar */}
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3 mb-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-5 h-5" />
              <input
                type="text"
                placeholder="Search..."
                className="w-full border pl-10 pr-3 py-2 focus:outline-none"
                value={search}
                onChange={(e) => {
                  setSearch(e.target.value);
                  setPage(1);
                }}
              />
            </div>
            <button
              onClick={exportCSV}
              className="flex items-center gap-2 bg-[#4E5C36] text-white px-4 py-2 shadow hover:bg-[#3e492a] transition"
            >
              <Download className="w-4 h-4" />
              Export CSV
            </button>
          </div>
          {/* Table */}
          <div className="overflow-x-auto bg-white shadow">
            {!isLoading && paginatedTickets?.length === 0 ? (
              <div className="flex flex-col items-center justify-center text-center py-10">
                <img
                  src={emptyTicket}
                  alt="Empty State"
                  className="w-32 h-32 object-contain mb-4 opacity-80"
                />
                <h3 className="text-lg font-semibold text-gray-700">
                  No tickets found.
                </h3>
                <p className="text-sm text-gray-500 mt-1">
                  When tickets are issued, they appear here.
                </p>
              </div>
            ) : (
              <table className="w-full border-collapse">
                <thead className="bg-gray-100 text-sm">
                  <tr>
                    <th className="p-3 text-left">DATE ISSUED</th>
                    {/* <th className="p-3 text-left">ORDER ID</th> */}
                    <th className="p-3 text-left">ATTENDEE NAME</th>
                    <th className="p-3 text-left">EMAIL</th>
                    <th className="p-3 text-left">DIETARY REQUIREMENTS</th>
                    <th className="p-3 text-left">TICKET TIER</th>
                    <th className="p-3 text-left">PRICE</th>
                    <th className="p-3 text-left">PAYMENT STATUS</th>
                  </tr>
                </thead>
                <tbody>
                  {isLoading &&
                    Array.from({ length: rowsPerPage }).map((_, i) => (
                      <SkeletonRow key={i} />
                    ))}
                  {!isLoading &&
                    paginatedTickets?.map((ticket) => {
                      const attendeeName = `${ticket.attendee.firstName} ${ticket.attendee.lastName}`;
                      const price =
                        parseFloat(ticket.orderId.amountTotal) /
                        (ticket.orderId.quantity || 1);
                      return (
                        <tr key={ticket._id} className="border-t">
                          <td className="p-3">
                            {new Date(ticket.createdAt).toLocaleDateString()}
                          </td>
                          {/* <td className="p-3">{ticket.orderId._id}</td> */}
                          <td className="p-3">{attendeeName}</td>
                          <td className="p-3">{ticket.attendee.email}</td>
                          <td className="p-3">
                            {ticket.attendee.dietaryRequirements || "N/A"}
                          </td>
                          <td className="p-3">
                            {slugToText(ticket.orderId.tier)}
                          </td>
                          <td className="p-3">£{(price / 100).toFixed(2)}</td>
                          <td className="p-3">
                            <span
                              className={`px-2 py-1 rounded-full text-sm font-semibold ${colors[ticket.orderId.paymentStatus]}`}
                            >
                              {ticket.orderId.paymentStatus}
                            </span>
                            {/* {ticket.orderId.paymentStatus} */}
                          </td>
                        </tr>
                      );
                    })}
                </tbody>
              </table>
            )}
          </div>
          {/* Pagination */}
          {!isLoading && (
            <div className="flex flex-col md:flex-row items-center justify-between mt-4 gap-3">
              <p className="text-sm text-gray-600">
                Showing {startIndex + 1} to{" "}
                {Math.min(startIndex + rowsPerPage, filteredTickets?.length)} of{" "}
                {filteredTickets?.length} results
              </p>
              <div className="flex items-center space-x-2">
                <select
                  value={rowsPerPage}
                  onChange={(e) => {
                    setRowsPerPage(Number(e.target.value));
                    setPage(1);
                  }}
                  className="border  px-2 py-1"
                >
                  {[5, 10, 20].map((size) => (
                    <option key={size} value={size}>
                      {size}
                    </option>
                  ))}
                </select>
                <button
                  disabled={page === 1}
                  onClick={() => setPage((p) => p - 1)}
                  className={`px-3 py-1  ${
                    page === 1
                      ? "bg-gray-200 text-gray-500 cursor-not-allowed"
                      : "bg-gray-300"
                  }`}
                >
                  Previous
                </button>
                <span className="px-2">
                  {page} / {totalPages || 1}
                </span>
                <button
                  disabled={page === totalPages}
                  onClick={() => setPage((p) => p + 1)}
                  className={`px-3 py-1  ${
                    page === totalPages
                      ? "bg-gray-200 text-gray-500 cursor-not-allowed"
                      : "bg-gray-300"
                  }`}
                >
                  Next
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
